import React, { useState } from 'react';
import { PageHeader } from '@components/layout/Breadcrumb';
import SectionHeader from '@components/common/SectionHeader';
import Card from '@components/common/Card';
import Input from '@components/forms/Input';
import TextArea from '@components/forms/TextArea';
import Select from '@components/forms/Select';
import Checkbox from '@components/forms/Checkbox';
import Button from '@components/common/Button';
import Alert from '@components/common/Alert';
import useForm from '@hooks/useForm';
import { validateMembershipForm } from '@utils/validators';
import { submitMembershipApplication } from '@services/membershipService';
import {
  FiCheckCircle,
  FiUsers,
  FiTrendingUp,
  FiDollarSign,
  FiBook,
  FiAward,
  FiPackage,
} from 'react-icons/fi';

/**
 * Membership Page Component
 * Membership benefits and application form
 */
const Membership = () => {
  const breadcrumbs = [{ label: 'Membership', path: '/membership' }];
  const [submitStatus, setSubmitStatus] = useState(null);

  // Initial form values
  const initialValues = {
    fullName: '',
    email: '',
    phone: '',
    organizationType: 'individual',
    organizationName: '',
    location: '',
    district: '',
    memberType: '',
    farmSize: '',
    mainCrop: '',
    yearsInFarming: '',
    interests: [],
    additionalInfo: '',
    acceptTerms: false,
  };

  // Form submission handler
  const handleSubmit = async (values) => {
    try {
      setSubmitStatus(null);
      
      // Submit to API
      await submitMembershipApplication(values);
      
      // Show success message
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your application! We will review it and contact you within 3-5 business days.',
      });
      
      // Reset form
      resetForm();
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to submit application. Please try again or contact us directly.',
      });
    }
  };

  // Use form hook
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit: onSubmit,
    resetForm,
    setFieldValue,
  } = useForm(initialValues, handleSubmit, validateMembershipForm);

  return (
    <div className="membership-page">
      {/* Page Header */}
      <PageHeader
        title="Become a Member"
        description="Join over 1,000 farmers benefiting from our cooperative services and collective marketing"
        breadcrumbs={breadcrumbs}
      />

      {/* Benefits Section */}
      <BenefitsSection />

      {/* Membership Types */}
      <MembershipTypes />

      {/* Application Form Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              subtitle="Join Us Today"
              title="Membership Application Form"
              description="Complete the form below to start your journey with Bukawa ACE Ltd"
              className="mb-8"
            />

            {/* Status Alert */}
            {submitStatus && (
              <Alert
                type={submitStatus.type}
                message={submitStatus.message}
                dismissible
                onDismiss={() => setSubmitStatus(null)}
                className="mb-6"
              />
            )}

            {/* Application Form */}
            <Card className="bg-bukawa-cream border border-bukawa-gray-200">
              <form onSubmit={onSubmit} noValidate>
                {/* Personal Information */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-bukawa-primary mb-4 flex items-center gap-2">
                    <FiUsers className="h-5 w-5" />
                    Personal Information
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Full Name"
                      name="fullName"
                      type="text"
                      placeholder="Enter your full name"
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.fullName}
                      touched={touched.fullName}
                      required
                      disabled={isSubmitting}
                    />

                    <Input
                      label="Email Address"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.email}
                      touched={touched.email}
                      required
                      disabled={isSubmitting}
                    />

                    <Input
                      label="Phone Number"
                      name="phone"
                      type="tel"
                      placeholder="+256 XXX XXX XXX"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.phone}
                      touched={touched.phone}
                      required
                      disabled={isSubmitting}
                      helper="Format: +256XXXXXXXXX or 07XXXXXXXX"
                    />

                    <Select
                      label="Application Type"
                      name="organizationType"
                      value={values.organizationType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.organizationType}
                      touched={touched.organizationType}
                      disabled={isSubmitting}
                      options={[
                        { value: 'individual', label: 'Individual Farmer' },
                        { value: 'organization', label: 'Cooperative/Organization' },
                      ]}
                    />

                    {values.organizationType === 'organization' && (
                      <Input
                        label="Organization Name"
                        name="organizationName"
                        type="text"
                        placeholder="Enter organization name"
                        value={values.organizationName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.organizationName}
                        touched={touched.organizationName}
                        required={values.organizationType === 'organization'}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                </div>

                {/* Location Information */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-bukawa-primary mb-4">
                    Location
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Village/Town"
                      name="location"
                      type="text"
                      placeholder="Enter your location"
                      value={values.location}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.location}
                      touched={touched.location}
                      required
                      disabled={isSubmitting}
                    />

                    <Select
                      label="District"
                      name="district"
                      placeholder="Select district"
                      value={values.district}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.district}
                      touched={touched.district}
                      disabled={isSubmitting}
                      options={[
                        { value: 'iganga', label: 'Iganga' },
                        { value: 'mayuge', label: 'Mayuge' },
                        { value: 'bugiri', label: 'Bugiri' },
                        { value: 'namutumba', label: 'Namutumba' },
                        { value: 'luuka', label: 'Luuka' },
                        { value: 'buyende', label: 'Buyende' },
                        { value: 'other', label: 'Other' },
                      ]}
                    />
                  </div>
                </div>

                {/* Farming Information */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-bukawa-primary mb-4">
                    Farming Details
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Select
                      label="Member Type"
                      name="memberType"
                      placeholder="Select type"
                      value={values.memberType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.memberType}
                      touched={touched.memberType}
                      required
                      disabled={isSubmitting}
                      options={[
                        { value: 'producer', label: 'Maize Producer' },
                        { value: 'both', label: 'Producer & Buyer' },
                        { value: 'buyer', label: 'Bulk Buyer' },
                      ]}
                    />

                    <Input
                      label="Farm Size (Acres)"
                      name="farmSize"
                      type="number"
                      placeholder="e.g., 5"
                      value={values.farmSize}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.farmSize}
                      touched={touched.farmSize}
                      disabled={isSubmitting}
                      helper="Approximate size of your farm"
                    />

                    <Input
                      label="Main Crop"
                      name="mainCrop"
                      type="text"
                      placeholder="e.g., Maize, Beans"
                      value={values.mainCrop}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.mainCrop}
                      touched={touched.mainCrop}
                      disabled={isSubmitting}
                    />

                    <Select
                      label="Years in Farming"
                      name="yearsInFarming"
                      placeholder="Select range"
                      value={values.yearsInFarming}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.yearsInFarming}
                      touched={touched.yearsInFarming}
                      disabled={isSubmitting}
                      options={[
                        { value: '0-2', label: '0-2 years' },
                        { value: '3-5', label: '3-5 years' },
                        { value: '6-10', label: '6-10 years' },
                        { value: '10+', label: '10+ years' },
                      ]}
                    />
                  </div>
                </div>

                {/* Interests */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-bukawa-primary mb-4">
                    Services of Interest
                  </h3>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      'Collective Marketing',
                      'Value Addition',
                      'Market Information',
                      'Input Supply',
                      'Agricultural Training',
                      'Post-Harvest Handling',
                    ].map((service) => (
                      <Checkbox
                        key={service}
                        label={service}
                        name="interests"
                        value={service}
                        checked={values.interests.includes(service)}
                        onChange={(e) => {
                          const newInterests = e.target.checked
                            ? [...values.interests, service]
                            : values.interests.filter((i) => i !== service);
                          setFieldValue('interests', newInterests);
                        }}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                </div>

                {/* Additional Information */}
                <TextArea
                  label="Additional Information (Optional)"
                  name="additionalInfo"
                  placeholder="Tell us more about your farming activities, expectations, or any questions you have..."
                  value={values.additionalInfo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.additionalInfo}
                  touched={touched.additionalInfo}
                  disabled={isSubmitting}
                  rows={4}
                  maxLength={500}
                  showCount
                />

                {/* Terms & Conditions */}
                <div className="mt-6 pt-6 border-t border-bukawa-gray-300">
                  <Checkbox
                    label={
                      <span>
                        I agree to the terms and conditions and confirm that the information provided is accurate.
                      </span>
                    }
                    name="acceptTerms"
                    checked={values.acceptTerms}
                    onChange={handleChange}
                    error={errors.acceptTerms}
                    touched={touched.acceptTerms}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end mt-8">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </Card>

            {/* Help Text */}
            <p className="text-sm text-bukawa-gray-600 text-center mt-6">
              Need help with your application?{' '}
              <a href="/contact" className="text-bukawa-primary hover:underline">
                Contact us
              </a>{' '}
              or call +256 786 142168
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />
    </div>
  );
};

/**
 * BenefitsSection Component
 */
const BenefitsSection = () => {
  const benefits = [
    {
      icon: FiTrendingUp,
      title: 'Better Prices',
      description: 'Access better markets and prices through collective bargaining and direct buyer linkages.',
    },
    {
      icon: FiPackage,
      title: 'Quality Inputs',
      description: 'Purchase certified seeds, fertilizers, and farming supplies at competitive bulk prices.',
    },
    {
      icon: FiBook,
      title: 'Training & Support',
      description: 'Free agricultural extension services, field demonstrations, and technical guidance.',
    },
    {
      icon: FiDollarSign,
      title: 'Financial Services',
      description: 'Access to credit facilities, savings schemes, and financial management training.',
    },
    {
      icon: FiAward,
      title: 'Value Addition',
      description: 'Process your maize into certified premium flour and access premium markets.',
    },
    {
      icon: FiUsers,
      title: 'Community Network',
      description: 'Join a network of 1,052+ farmers and 10 cooperatives for knowledge sharing.',
    },
  ];

  return (
    <section className="section bg-bukawa-light">
      <div className="container-custom">
        <SectionHeader
          subtitle="Why Join Us"
          title="Member Benefits"
          description="Discover how membership in Bukawa ACE Ltd can transform your farming business"
          className="mb-16"
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card
                key={index}
                hover
                className="text-center bg-white border border-bukawa-gray-200"
              >
                <div className="w-16 h-16 bg-bukawa-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="h-8 w-8 text-bukawa-primary" />
                </div>
                <h3 className="text-xl font-semibold text-bukawa-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-bukawa-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/**
 * MembershipTypes Component
 */
const MembershipTypes = () => {
  return (
    <section className="section bg-white">
      <div className="container-custom">
        <SectionHeader
          subtitle="Membership Options"
          title="Choose Your Membership Type"
          className="mb-12"
        />

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Individual Membership */}
          <Card className="bg-bukawa-primary-50 border-2 border-bukawa-primary-200">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-bukawa-primary text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <FiUsers className="h-10 w-10" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-bukawa-primary mb-2">
                Individual Farmer
              </h3>
              <p className="text-bukawa-gray-600">
                For individual maize producers
              </p>
            </div>

            <ul className="space-y-3 mb-6">
              {[
                'Full access to all services',
                'Collective marketing support',
                'Agricultural training',
                'Input supply discounts',
                'Post-harvest handling',
                'Market information',
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <FiCheckCircle className="h-5 w-5 text-bukawa-primary flex-shrink-0 mt-0.5" />
                  <span className="text-bukawa-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Organization Membership */}
          <Card className="bg-bukawa-secondary-50 border-2 border-bukawa-secondary-200">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-bukawa-secondary text-bukawa-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAward className="h-10 w-10" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-bukawa-secondary-700 mb-2">
                Cooperative/Organization
              </h3>
              <p className="text-bukawa-gray-600">
                For primary cooperatives & organizations
              </p>
            </div>

            <ul className="space-y-3 mb-6">
              {[
                'Bulk marketing services',
                'Organizational capacity building',
                'Group training programs',
                'Priority access to inputs',
                'Collective bargaining power',
                'Network partnerships',
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <FiCheckCircle className="h-5 w-5 text-bukawa-secondary-700 flex-shrink-0 mt-0.5" />
                  <span className="text-bukawa-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </section>
  );
};

/**
 * TestimonialsSection Component
 */
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'John Mukasa',
      role: 'Maize Farmer, Nakigo',
      quote: 'Joining Bukawa has transformed my farming. I now get better prices for my maize and access to quality seeds at affordable rates.',
    },
    {
      name: 'Sarah Nambi',
      role: 'Member, Wairama Cooperative',
      quote: 'The training and support from Bukawa have improved my farming techniques. My yields have increased by 40% in just two seasons.',
    },
    {
      name: 'David Walusimbi',
      role: 'Cooperative Leader',
      quote: 'Bukawa has been instrumental in helping our cooperative access better markets. The collective marketing approach really works!',
    },
  ];

  return (
    <section className="section bg-bukawa-cream">
      <div className="container-custom">
        <SectionHeader
          subtitle="Member Stories"
          title="What Our Members Say"
          className="mb-12"
        />

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="bg-white">
              <div className="mb-4">
                <div className="flex gap-1 text-bukawa-secondary mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="h-5 w-5 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-bukawa-gray-700 italic mb-4">
                  "{testimonial.quote}"
                </p>
              </div>
              <div>
                <p className="font-semibold text-bukawa-gray-900">
                  {testimonial.name}
                </p>
                <p className="text-sm text-bukawa-gray-600">
                  {testimonial.role}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Membership;